/** The panes that exist whatever the project's grouping levels are. */
export const NAMED_ANALYTICS_TABS = [
    'summary',
    'phase-bridge',
    'by-scenario',
    'coverage-matrix',
    'split-heads',
    'stranded',
    /*
      LAST because it is NEW, not ported: the frozen app kept spans & layers in a modal of its own
      behind a toolbar button, so there is no legacy tab position to honour — and the six above keep
      theirs, which is what the ordering promise is actually about. The engine has been in core since
      P2 (`computeSpansLayers`, parity-tested); this tab is the first surface to draw it.
    */
    'spans-layers',
    /*
      Also new, also after the ported six. The frozen app never had a scenario comparison anywhere —
      a carve-out decision IS a comparison of options, and until the `/scenario-compare` transport the
      client could not price a sibling design at all. Conditional (see `analyticsTabs`): a project's
      tab bar only carries it when the caller knows there is more than one scenario to compare.
    */
    'compare-scenarios',
    /*
      Newest, and last for the same reason as the two above it. It answers a question the frozen app
      could not ask at all: the slider shows what ONE threshold does, and this shows what every
      threshold the rule can store would do — the sensitivity view an Excel team cannot afford to
      build by hand, because it means rebuilding the model at every step.
    */
    'threshold-sweep',
    /*
      Last, and the only pane in the modal that is not a function of the design at all. Every other
      tab re-derives the state on screen; this one reads a ledger the design cannot reconstruct,
      because a roster refresh destroys the mitigation plan in the same transaction that records the
      departure. It is offered to every project, empty ones included — "nobody has left yet" is a real
      answer about a deal in design, and withholding the tab would make it look like the product does
      not track this.
    */
    'variance',
];
const NAMED_LABELS = {
    summary: 'Separation summary',
    'phase-bridge': 'Phase bridge',
    'by-scenario': 'Perspective comparison',
    'coverage-matrix': 'Coverage matrix',
    'split-heads': 'Dedication',
    stranded: 'Stranded cost',
    'spans-layers': 'Spans & layers',
    'compare-scenarios': 'Scenario comparison',
    'threshold-sweep': 'Perimeter sensitivity',
    variance: 'Plan vs actual',
};
/**
 * Which panes are a statement about MONEY, and therefore cannot be drawn without compensation.
 *
 * A MEMBER WITH SALARY WITHHELD SEES ZEROES, not blanks — `p.salary` is `number | null` and every
 * cost function in `economics.ts` reads `Number(p.salary) || 0`. That is correct behaviour for a
 * project whose file had no salary column, which the product already handles and says so about
 * (`diagnostics.ts`, and `analytics.ts`'s `anySalary`). It is the WRONG behaviour here, because these
 * panes would then report a stranded cost of zero rather than no stranded cost — and a pane of
 * zeroes carried into a steering committee is a wrong answer, where a pane that is not offered is a
 * boundary somebody can see.
 *
 * So they are omitted. That is this file's own rule, stated at `analyticsTabs`: "a tab that cannot
 * render is worse than one that is not offered."
 *
 * THE LEVEL TABS STAY, and the asymmetry is deliberate rather than an oversight. A level breakdown is
 * a headcount pane with a cost column; the metric toggle simply loses `cost`, and the counts are
 * still true. The marked panes have no version of themselves without money in them.
 *
 * `satisfies` MAKES A NEW PANE A BUILD ERROR naming it, the shape `FIELD_POLICY` uses. A pane added
 * later that quietly defaulted to "safe" would be the exact failure this table exists to prevent.
 */
export const TAB_NEEDS_SALARY = {
    /** Loaded cost per FTE, stranded capacity in money. */
    stranded: true,
    /** The people P&L bridge to standalone — the number the whole product is for. */
    summary: true,
    /** Cost by phase. */
    'phase-bridge': true,
    'by-scenario': false,
    'coverage-matrix': false,
    'split-heads': false,
    /** Structure, not money: layers and spans are counts of nodes, true without a salary column. */
    'spans-layers': false,
    /** Three of its five columns price the options. The server refuses a withheld grant the same way. */
    'compare-scenarios': true,
    /** Three of its four axes are money, and a curve of zeroes is a shape as well as a number. */
    'threshold-sweep': true,
    /**
     * Months, actions and names — no money anywhere, so a withheld grant reads it whole.
     *
     * The route agrees and does not gate it either, which is the point of stating it twice: the same
     * argument, in the two places that could otherwise drift into disagreeing about one pane.
     */
    variance: false,
};
/**
 * Every pane this project has, in the app's own order: the levels first, then the named ones.
 *
 * The order is the app's so that somebody arriving from the frozen tool finds each tab where it was
 * (orgchart.html:7254-7265). `compare-scenarios` is offered only when the caller says the project
 * has more than one scenario — its pane fetches every sibling's priced row from
 * `/scenario-compare`, and a comparison of one option is a table restating the screen. Absent
 * rather than disabled when the count is unknown or one, because a tab that cannot render is worse
 * than one that is not offered. A member whose grant withholds compensation loses the four money
 * panes for the same reason, in the same way.
 */
export function analyticsTabs(state, context = {}) {
    const levels = state.groupingLevels.map((level) => ({
        key: `level-${level.key}`,
        label: `By ${level.label}`,
        levelKey: level.key,
    }));
    const named = NAMED_ANALYTICS_TABS.filter((key) => {
        if (state.scope?.salaryWithheld === true && TAB_NEEDS_SALARY[key])
            return false;
        if (key === 'compare-scenarios' && (context.scenarioCount ?? 0) < 2)
            return false;
        return true;
    });
    return [...levels, ...named.map((key) => ({ key, label: NAMED_LABELS[key] }))];
}
/**
 * The tab to show: the one asked for if it still exists, otherwise the project's default.
 *
 * Called wherever the phase spine calls `resolvePhase` — on set, and on every refetch — because the tab
 * you are on can be removed by somebody else's command while your modal is open.
 *
 * The default is the app's (the second grouping level, or the first when there is only one), with one
 * correction: the app computes `Math.min(1, levels.length - 1)` and with NO levels that is -1, so no tab
 * is marked active and it renders a pane nobody selected (orgchart.html:7254, :7275-7276). The control
 * disagrees with the content, which is the defect the P4i rule exists to stop. Here a project with no
 * grouping levels opens on the summary, which is the one pane that needs neither levels nor siblings.
 */
export function resolveAnalyticsTab(state, tab, context = {}) {
    const tabs = analyticsTabs(state, context);
    if (tabs.some((t) => t.key === tab))
        return tab;
    return defaultAnalyticsTab(state, context);
}
export function defaultAnalyticsTab(state, context = {}) {
    const levels = state.groupingLevels;
    if (levels.length > 0)
        return `level-${levels[Math.min(1, levels.length - 1)].key}`;
    /*
      A PROJECT WITH NO GROUPING LEVELS OPENS ON THE SUMMARY — unless the summary is one of the panes a
      withheld grant does not get, which is exactly the case this line exists for. Returning it anyway
      would put the modal on a tab that is not in its own tab list: no control marked active, and a pane
      rendered that nobody selected. That is the defect the P4i rule exists to stop, arriving through a
      new door, and it is why this resolves against the OFFERED set rather than a constant.
    */
    const offered = analyticsTabs(state, context);
    return offered.find((t) => t.key === 'summary')?.key ?? offered[0]?.key ?? 'summary';
}
/** The grouping level a level tab names, or null for a named pane or a level since removed. */
export function levelOfTab(state, tab) {
    if (!tab.startsWith('level-'))
        return null;
    const key = tab.slice('level-'.length);
    return state.groupingLevels.find((l) => l.key === key) ?? null;
}
//# sourceMappingURL=analytics-tabs.js.map